import {
  COVERAGE_FILL_CLASS,
  COVERAGE_STATES,
  type CoverageCounts,
  coverageTotal,
  describeCoverage,
} from "./coverage";

const HEIGHT_CLASS = { sm: "h-1.5", md: "h-2", lg: "h-3" } as const;

export interface CoverageBarProps {
  coverage: CoverageCounts;
  /** `sm` for a table of contents, `md` for a card, `lg` for a headline bar. */
  size?: keyof typeof HEIGHT_CLASS;
  /**
   * Expose the bar to assistive tech. Turn off when a visible breakdown or a
   * labelled trigger already says the same thing.
   */
  labelled?: boolean;
}

/** A stacked bar of how many steps fall in each coverage state. */
export function CoverageBar({
  coverage,
  size = "sm",
  labelled = true,
}: CoverageBarProps) {
  const total = coverageTotal(coverage);
  const className = `flex w-full overflow-hidden rounded-full bg-(--bg-canvas) ${HEIGHT_CLASS[size]}`;
  const segments =
    total > 0 &&
    COVERAGE_STATES.filter((state) => coverage[state] > 0).map((state) => (
      <span
        key={state}
        className={`h-full ${COVERAGE_FILL_CLASS[state]}`}
        style={{ width: `${(coverage[state] / total) * 100}%` }}
      />
    ));
  return labelled ? (
    <span
      role="img"
      aria-label={describeCoverage(coverage)}
      className={className}
    >
      {segments}
    </span>
  ) : (
    <span aria-hidden={true} className={className}>
      {segments}
    </span>
  );
}
